import { PageSpinner } from '@branch-messenger/willow-ui'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { FC, useCallback, useEffect, useMemo, useRef } from 'react'

import { parseTabapayData } from '@/lib/tabapay-data-parser'
import {
  onDemandPayCardQueryOptions,
  useCreateDebitCard,
  useDeleteDebitCard,
  useVerifyDebitCard,
} from '@/queries/card'
import { paymentProfileQueryOptions } from '@/queries/profile'
import { CreateDebitCardInfo, DebitCardType } from '@/types/card'

import { CreateDebitCardContainer } from './styles'

interface Props {
  type: DebitCardType
  handleClose?: () => void
  handleSuccess?: () => void
  handleError?: () => void
  handleOnLoad?: () => void
}

export const DebitCardForm: FC<Props> = ({
  type,
  handleClose,
  handleSuccess,
  handleError,
  handleOnLoad,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const { mutate: createDebitCard, isPending: creatingDebitCard } =
    useCreateDebitCard()
  const { mutate: deleteDebitCard, isPending: deletingDebitCard } =
    useDeleteDebitCard()
  const { data: paymentProfileData } = useSuspenseQuery(
    paymentProfileQueryOptions
  )
  const { data: onDemandPaycard } = useQuery(onDemandPayCardQueryOptions())
  const { mutate: verifyCard } = useVerifyDebitCard()

  const isPending = useMemo(
    () => creatingDebitCard || deletingDebitCard,
    [creatingDebitCard, deletingDebitCard]
  )

  const handleCreateDebitCard = useCallback(
    (createCardRequestInfo: CreateDebitCardInfo) =>
      createDebitCard(
        { type, requestBody: createCardRequestInfo },
        {
          onSuccess: handleSuccess,
          onError: handleError,
        }
      ),
    [createDebitCard, type, handleSuccess, handleError]
  )

  const handleUpdateDebitCard = useCallback(
    (accountId: number, createCardRequestInfo: CreateDebitCardInfo) =>
      deleteDebitCard(
        {
          type,
          accountId,
        },
        {
          onSuccess: () => handleCreateDebitCard(createCardRequestInfo),
        }
      ),
    [deleteDebitCard, type, handleCreateDebitCard]
  )

  const handleVerifyCard = useCallback(
    (createCardRequestInfo: CreateDebitCardInfo) =>
      verifyCard(
        { requestBody: createCardRequestInfo },
        {
          onSuccess: () => {
            const existingCard =
              type === 'PAYOUT_CARD'
                ? paymentProfileData.branch_direct_card_account
                : onDemandPaycard

            if (existingCard) {
              handleUpdateDebitCard(existingCard.id, createCardRequestInfo)
            } else {
              handleCreateDebitCard(createCardRequestInfo)
            }
          },
        }
      ),
    [
      verifyCard,
      type,
      paymentProfileData,
      onDemandPaycard,
      handleUpdateDebitCard,
      handleCreateDebitCard,
    ]
  )

  useEffect(() => {
    const handleWindowMessage = (event: MessageEvent) => {
      if (event.source === iframeRef.current?.contentWindow) {
        if (event.data === 'Close') {
          return handleClose?.()
        }
        const { createCardRequestInfo } = parseTabapayData(event.data)
        handleVerifyCard(createCardRequestInfo)
      }
    }

    window.addEventListener('message', handleWindowMessage)

    return () => {
      window.removeEventListener('message', handleWindowMessage)
    }
  }, [handleClose, handleVerifyCard])

  return (
    <CreateDebitCardContainer>
      <PageSpinner open={isPending} />
      <iframe
        src={import.meta.env.VITE_TABAPAY_IFRAME_URL}
        title="Debit Account Form"
        ref={iframeRef}
        onLoad={handleOnLoad}
      />
    </CreateDebitCardContainer>
  )
}
